/**
 * The top of Home inside the app shell (design agreed 29 Sep 2026):
 *
 *   [ 🔍 Search courses and library                        ⌘K ]
 *   ┌ Create ──────────┐ ┌ Open by code ─────┐ ┌ Explore ──────────┐
 *   │ New course  ✦ AI │ │ [XXXXXX]  →       │ │ Courses  Library  │
 *   └──────────────────┘ └───────────────────┘ └───────────────────┘
 *
 * A Learner-only member has no Create card and no "Courses" button.
 * ⌘K / Ctrl K focuses the search from anywhere on the page.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { T } from './orgTheme'
import { spaLink } from '@/lib/spaLink'
import { useOpenCourseCode } from '@/lib/useOpenCourseCode'

/** The three cards' accents (15 Oct 2026, “Cobalt — cobalt / orange / emerald”). */
const K = { cobalt: '#2F55D4', cobaltBg: '#E8EEFD', cobaltHover: '#DCE5FC', orange: '#E0661F', orangeBg: '#FDEEE2', emerald: '#2F8F63', emeraldBg: '#E3F3EB' }

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

export function HomeTop({ search, onSearch, placeholder, showCards, learnerOnly }: {
  search: string
  onSearch: (v: string) => void
  placeholder: string
  showCards: boolean
  learnerOnly: boolean
}) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [code, setCode] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); inputRef.current?.focus() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Launched to you → your course (/learn); otherwise the public player.
  const { open: openByCode } = useOpenCourseCode('/')
  const openCode = () => openByCode(code)

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '56px 24px 4px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 860 }}>
          <svg width="18" height="18" viewBox="0 0 20 20" style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: T.faint }}>
            <circle cx="8.5" cy="8.5" r="6" stroke="currentColor" strokeWidth="1.8" fill="none" /><path d="M13.2 13.2l4.3 4.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input ref={inputRef} value={search} onChange={e => onSearch(e.target.value)} placeholder={placeholder} aria-label="Search"
            className="focus:border-[#7A3BD9] focus:shadow-[0_0_0_4px_#EFE6FC]"
            style={{ width: '100%', height: 58, padding: '0 72px 0 50px', border: `1px solid ${T.border}`, borderRadius: 999, background: '#fff', fontSize: 16, color: T.dark, outline: 'none', boxShadow: '0 1px 2px rgba(28,26,34,.04)' }} />
          <span style={{ position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', padding: '2px 7px', border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 11.5, color: T.faint, background: '#fff' }}>{isMac ? '⌘K' : 'Ctrl K'}</span>
        </div>
      </div>

      {showCards && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, width: '100%', maxWidth: 1080, margin: '0 auto' }}>
          {!learnerOnly && (
            <Card icon={<span style={{ fontSize: 22, fontWeight: 300, color: K.cobalt, lineHeight: 1 }}>+</span>} iconBg={K.cobaltBg}
              title="Create" text="Build a new interactive course.">
              {/* One action (7 Oct 2026) — "New course" and "✦ AI" opened the same wizard. */}
              <a {...spaLink('/course/new', () => navigate('/course/new'))} className="hover:!bg-[#DCE5FC]"
                style={{ height: 42, borderRadius: 10, background: K.cobaltBg, color: K.cobalt, fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none' }}>
                Create course <span aria-hidden style={{ fontSize: 16 }}>→</span>
              </a>
            </Card>
          )}

          <Card icon={<span style={{ fontSize: 17, color: K.orange }}>→</span>} iconBg={K.orangeBg}
            title="Open by code" text="Got a 6-character course code?">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 42, border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '0 5px 0 14px', background: '#fff' }}>
              <input value={code} maxLength={6} placeholder="X X X X X X" aria-label="Course code"
                onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                onKeyDown={e => e.key === 'Enter' && openCode()}
                style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'ui-monospace, monospace', fontSize: 14, letterSpacing: '.18em', color: T.dark }} />
              <button onClick={openCode} disabled={code.length < 6} aria-label="Open course"
                style={{ width: 34, height: 32, borderRadius: 7, border: 'none', background: K.cobaltBg, color: K.cobalt, fontSize: 15, cursor: code.length < 6 ? 'default' : 'pointer', opacity: code.length < 6 ? 0.6 : 1 }}>→</button>
            </div>
          </Card>

          <Card icon={<LibraryGlyph color={K.emerald} />} iconBg={K.emeraldBg}
            title="Explore" text={learnerOnly ? 'Curated collections from Learnbee.' : 'Your courses and curated collections.'}>
            <div style={{ display: 'flex', gap: 10 }}>
              {!learnerOnly && <OutlineBtn href="/courses" onClick={() => navigate('/courses')}><BookGlyph /> Courses</OutlineBtn>}
              <OutlineBtn href="/library" onClick={() => navigate('/library')}><LibraryGlyph color={T.dark} /> Library</OutlineBtn>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

function Card({ icon, iconBg, title, text, children }: { icon: ReactNode; iconBg: string; title: string; text: string; children: ReactNode }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: '22px 22px 24px', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 1px 2px rgba(28,26,34,.03)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ width: 42, height: 42, borderRadius: 10, background: iconBg, display: 'grid', placeItems: 'center', flex: 'none' }}>{icon}</span>
        <span style={{ fontSize: 18, fontWeight: 600, color: T.dark }}>{title}</span>
      </div>
      <span style={{ fontSize: 15, color: T.muted }}>{text}</span>
      {children}
    </div>
  )
}

function OutlineBtn({ href, onClick, children }: { href: string; onClick: () => void; children: ReactNode }) {
  return (
    <a {...spaLink(href, onClick)} className="hover:border-[#7A3BD9]"
      style={{ flex: 1, height: 42, borderRadius: 10, border: `1px solid ${T.inputBorder}`, background: '#fff', color: T.dark, fontSize: 15, fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none' }}>
      {children}
    </a>
  )
}

function BookGlyph() {
  return <svg width="15" height="15" viewBox="0 0 18 18"><path d="M2 3.5c2.5-.8 5-.6 7 1v10c-2-1.6-4.5-1.8-7-1v-10zM16 3.5c-2.5-.8-5-.6-7 1v10c2-1.6 4.5-1.8 7-1v-10z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round" /></svg>
}

function LibraryGlyph({ color }: { color: string }) {
  return <svg width="18" height="18" viewBox="0 0 22 22" style={{ color }}><path d="M4 4v14M8 4v14M12 4v14M15.5 4.5l3.5 13.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
}
